// orchestrator_fullstack/frontend/src/app/admin/observability/AdminObservabilityPolicyFeedbackPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import TimeSeriesChart from "./components/TimeSeriesChart";
import TopKTable from "./components/TopKTable";
import { promQuery, promQueryRange, rangeWindowMs, toSeries } from "./prom";
import { getTenantUuid } from "@/app/ingress/tenant";

type Series = {
  name: string;
  points: Array<{ t: number; v: number }>;
};

function num(v: any): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default function AdminObservabilityPolicyFeedbackPage() {
  const tenantUuid = getTenantUuid();

  const [policySeries, setPolicySeries] = useState<Series[]>([]);
  const [policyTop, setPolicyTop] = useState<Array<Record<string, any>>>([]);
  const [feedbackRows, setFeedbackRows] = useState<Array<Record<string, any>>>([]);
  const [incidentRows, setIncidentRows] = useState<Array<Record<string, any>>>([]);
  const [unsafeRows, setUnsafeRows] = useState<Array<Record<string, any>>>([]);
  const [err, setErr] = useState<string | null>(null);

  const policyTopColumns = useMemo(
    () => [
      { key: "slug", label: "Slug" },
      { key: "action", label: "Action" },
      { key: "events_15m", label: "Events (15m)" },
    ],
    []
  );

  const feedbackTopColumns = useMemo(
    () => [
      { key: "kind", label: "Kind" },
      { key: "category", label: "Category" },
      { key: "events_1h", label: "Events (1h)" },
    ],
    []
  );

  const incidentTopColumns = useMemo(
    () => [
      { key: "category", label: "Category" },
      { key: "incidents_24h", label: "Incidents (24h)" },
    ],
    []
  );

  const unsafeTopColumns = useMemo(
    () => [
      { key: "tag", label: "Tag" },
      { key: "events_1h", label: "Events (1h)" },
    ],
    []
  );

  useEffect(() => {
    let alive = true;

    async function run() {
      setErr(null);

      // -----------------------
      // Policy allow/block time series (rate over 5m, chart last 6h)
      // -----------------------
      try {
        const { start, end } = rangeWindowMs(6);
        const q = `sum by (action,slug) (rate(policy_decisions_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m]))`;
        const resp = await promQueryRange(q, tenantUuid, { start, end, step: "60s" });
        const ss = toSeries(resp);

        const series: Series[] = ss.map((s) => ({
          name: `${s.metric.slug || "unknown"} · ${s.metric.action || "unknown"}`,
          points: s.points,
        }));

        if (!alive) return;
        setPolicySeries(series);
      } catch (e: any) {
        if (!alive) return;
        setPolicySeries([]);
        setErr((prev) => prev || (e?.message ? `Policy series: ${String(e.message)}` : "Policy series failed"));
      }

      // -----------------------
      // Top policy decisions (15m)
      // -----------------------
      try {
        const q = `topk(10, sum by (slug,action) (increase(policy_decisions_total_by_tenant_total{tenant_id="${tenantUuid}"}[15m])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);

        const rows = ss.map((s) => ({
          slug: s.metric.slug || "unknown",
          action: s.metric.action || "unknown",
          events_15m: num(s.points?.[0]?.v),
        }));

        if (!alive) return;
        setPolicyTop(rows.sort((a, b) => num(b.events_15m) - num(a.events_15m)));
      } catch (e: any) {
        if (!alive) return;
        setPolicyTop([]);
        setErr((prev) => prev || (e?.message ? `Policy top: ${String(e.message)}` : "Policy top failed"));
      }

      // -----------------------
      // Feedback (if/when emitted): feedback_events_total by kind/category (1h)
      // -----------------------
      try {
        const q = `topk(10, sum by (kind,category) (increase(feedback_events_total_by_tenant_total{tenant_id="${tenantUuid}"}[1h])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);

        const rows = ss.map((s) => ({
          kind: s.metric.kind || "unknown",
          category: s.metric.category || "unknown",
          events_1h: num(s.points?.[0]?.v),
        }));

        if (!alive) return;
        setFeedbackRows(rows.sort((a, b) => num(b.events_1h) - num(a.events_1h)));
      } catch (e: any) {
        if (!alive) return;
        // Not an error if you haven't wired feedback metrics yet; keep empty.
        setFeedbackRows([]);
      }

      // -----------------------
      // Incidents (if/when emitted): feedback_incidents_total by category (24h)
      // -----------------------
      try {
        const q = `topk(10, sum by (category) (increase(feedback_incidents_total_by_tenant_total{tenant_id="${tenantUuid}"}[24h])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);

        const rows = ss.map((s) => ({
          category: s.metric.category || "unknown",
          incidents_24h: num(s.points?.[0]?.v),
        }));

        if (!alive) return;
        setIncidentRows(rows.sort((a, b) => num(b.incidents_24h) - num(a.incidents_24h)));
      } catch {
        setIncidentRows([]);
      }

      // -----------------------
      // Unsafe spikes (best-effort)
      // If you later add a counter like unsafe_tag_total{tag=...}, this lights up.
      // -----------------------
      try {
        const q = `topk(10, sum by (tag) (increase(unsafe_tag_total[1h])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);

        const rows = ss.map((s) => ({
          tag: s.metric.tag || "unknown",
          events_1h: num(s.points?.[0]?.v),
        }));

        if (!alive) return;
        setUnsafeRows(rows.sort((a, b) => num(b.events_1h) - num(a.events_1h)));
      } catch {
        setUnsafeRows([]);
      }
    }

    run();
    const t = window.setInterval(run, 25_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [tenantUuid]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xl font-semibold">Observability · Policy & Feedback</div>
          <div className="text-xs text-gray-500 mt-1">
            Powered by Prometheus via Admin proxy. Tenant: <code>{tenantUuid}</code>
          </div>
        </div>

        <div className="text-xs text-gray-500">
          Auto-refresh: <code>25s</code>
        </div>
      </div>

      {err ? <div className="text-sm text-red-700 whitespace-pre-wrap">{err}</div> : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TimeSeriesChart title="Policy decisions rate (5m)" series={policySeries} />

        <div className="rounded border bg-white p-4">
          <div className="font-medium">Top policy events (15m)</div>
          <div className="text-xs text-gray-500 mt-1">
            PromQL: <code>topk(10, sum by (slug,action) (increase(policy_decisions_total[15m])))</code>
          </div>
          <div className="mt-3">
            <TopKTable columns={policyTopColumns} rows={policyTop} emptyText="No policy metrics found." />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded border bg-white p-4">
          <div className="font-medium">Feedback events (1h)</div>
          <div className="text-xs text-gray-500 mt-1">
            PromQL: <code>topk(10, sum by (kind,category) (increase(feedback_events_total[1h])))</code>
          </div>
          <div className="mt-3">
            <TopKTable columns={feedbackTopColumns} rows={feedbackRows} emptyText="No feedback metrics found." />
          </div>
        </div>

        <div className="rounded border bg-white p-4">
          <div className="font-medium">Incidents (24h)</div>
          <div className="text-xs text-gray-500 mt-1">
            PromQL: <code>topk(10, sum by (category) (increase(feedback_incidents_total[24h])))</code>
          </div>
          <div className="mt-3">
            <TopKTable columns={incidentTopColumns} rows={incidentRows} emptyText="No incident metrics found." />
          </div>
        </div>
      </div>

      <div className="rounded border bg-white p-4">
        <div className="font-medium">Unsafe tag spikes (1h)</div>
        <div className="text-xs text-gray-500 mt-1">
          PromQL: <code>topk(10, sum by (tag) (increase(unsafe_tag_total[1h])))</code>
          <span className="ml-2">
            (This appears once you emit <code>unsafe_tag_total</code>.)
          </span>
        </div>
        <div className="mt-3">
          <TopKTable columns={unsafeTopColumns} rows={unsafeRows} emptyText="No unsafe tag metrics found." />
        </div>
      </div>
    </div>
  );
}